import { useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { magicLinkSchema } from '@/lib/validations'
import { login, requestMagicLink } from '@/lib/api/auth'
import { useAuth } from '@/lib/auth-context'
import { Mail, Lock, Eye, EyeOff } from 'lucide-react'

// Password login schema
const passwordLoginSchema = z.object({
  email: z.string().email('Adresse email invalide'),
  password: z.string().min(1, 'Le mot de passe est requis'),
})

type PasswordLoginFormData = z.infer<typeof passwordLoginSchema>
type MagicLinkFormData = z.infer<typeof magicLinkSchema>

type LoginMode = 'password' | 'magiclink'

export default function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { login: authLogin } = useAuth()
  const [mode, setMode] = useState<LoginMode>('password')
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Password login form
  const passwordForm = useForm<PasswordLoginFormData>({
    resolver: zodResolver(passwordLoginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  // Magic link form
  const magicLinkForm = useForm<MagicLinkFormData>({
    resolver: zodResolver(magicLinkSchema),
    defaultValues: {
      email: '',
    },
  })

  // Handle error messages from URL params
  const urlError = searchParams.get('error')
  const errorMessages: Record<string, string> = {
    missing_token: 'Lien de connexion manquant',
    invalid_token: 'Lien de connexion invalide',
    expired_token: 'Le lien a expiré. Demandez-en un nouveau.',
    server_error: 'Une erreur est survenue. Réessayez.',
  }

  const onPasswordLogin = async (data: PasswordLoginFormData) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await login(data.email, data.password)
      authLogin(response)
      navigate('/home')
    } catch (err: unknown) {
      const errData = (err instanceof Error && 'response' in err)
        ? (err as unknown as { response?: { data?: { ErrorCode?: string; Message?: string } } }).response?.data
        : undefined
      const errorCode = errData?.ErrorCode
      const errorMessage = errData?.Message

      if (errorCode === 'NoPasswordSet') {
        setError('Aucun mot de passe défini. Utilisez "Mot de passe oublié ?" pour en créer un.')
      } else if (errorCode === 'AccountLockedOut') {
        setError('Compte temporairement verrouillé. Réessayez dans 15 minutes.')
      } else if (errorMessage) {
        setError(errorMessage)
      } else {
        setError('Email ou mot de passe incorrect')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onMagicLinkRequest = async (data: MagicLinkFormData) => {
    setIsLoading(true)
    setError(null)

    try {
      await requestMagicLink(data.email)
      navigate(`/login/sent?email=${encodeURIComponent(data.email)}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue')
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and tagline */}
        <div className="text-center mb-12">
          <h1 className="text-6xl font-bold text-primary mb-4">SOLY</h1>
          <div className="w-32 h-1.5 bg-primary mx-auto rounded-full mb-6"></div>
          <p className="text-lg text-muted-foreground font-medium">
            Simplifiez votre gestion de projets
          </p>
        </div>

        {/* Login card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          {/* Error messages */}
          {(error || urlError) && (
            <div data-testid="login-error" className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <span className="text-red-600 text-lg">⚠️</span>
              <p className="text-sm text-red-600 flex-1">
                {error || errorMessages[urlError!] || 'Une erreur est survenue'}
              </p>
            </div>
          )}

          {mode === 'password' ? (
            /* Password Login Form */
            <form onSubmit={passwordForm.handleSubmit(onPasswordLogin)} className="space-y-6">
              {/* Email input */}
              <div className="space-y-3">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="email"
                    data-testid="login-email-input"
                    type="email"
                    placeholder="Votre adresse email"
                    autoComplete="username"
                    {...passwordForm.register('email')}
                    autoFocus
                    className={`pl-12 h-14 text-base ${passwordForm.formState.errors.email ? 'border-red-500 animate-shake' : ''}`}
                  />
                </div>
                {passwordForm.formState.errors.email && (
                  <p className="text-sm text-red-600 flex items-center gap-1 animate-shake">
                    <span>⚠️</span>
                    <span>{passwordForm.formState.errors.email.message}</span>
                  </p>
                )}
              </div>

              {/* Password input */}
              <div className="space-y-3">
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Votre mot de passe"
                    autoComplete="current-password"
                    {...passwordForm.register('password')}
                    className={`pl-12 pr-12 h-14 text-base ${passwordForm.formState.errors.password ? 'border-red-500 animate-shake' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {passwordForm.formState.errors.password && (
                  <p className="text-sm text-red-600 flex items-center gap-1 animate-shake">
                    <span>⚠️</span>
                    <span>{passwordForm.formState.errors.password.message}</span>
                  </p>
                )}
              </div>

              {/* Submit button */}
              <Button
                data-testid="login-submit-btn"
                type="submit"
                className="w-full h-12 text-base font-semibold"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="inline-block animate-spin mr-2">⏳</span>
                    Connexion...
                  </>
                ) : (
                  'Se connecter'
                )}
              </Button>

              {/* Forgot password link */}
              <div className="text-center">
                <Link
                  data-testid="login-forgot-password-link"
                  to="/forgot-password"
                  className="text-sm text-primary hover:underline font-medium"
                >
                  Mot de passe oublié ?
                </Link>
              </div>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-muted-foreground">OU</span>
                </div>
              </div>

              {/* Switch to magic link */}
              <Button
                data-testid="login-magic-link-btn"
                type="button"
                variant="outline"
                onClick={() => setMode('magiclink')}
                className="w-full h-12 text-base"
              >
                Se connecter avec un lien magique
              </Button>
            </form>
          ) : (
            /* Magic Link Form */
            <form onSubmit={magicLinkForm.handleSubmit(onMagicLinkRequest)} className="space-y-6">
              {/* Email input */}
              <div className="space-y-3">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="magic-email"
                    data-testid="login-magic-email-input"
                    type="email"
                    placeholder="Votre adresse email"
                    {...magicLinkForm.register('email')}
                    autoFocus
                    className={`pl-12 h-14 text-base ${magicLinkForm.formState.errors.email ? 'border-red-500 animate-shake' : ''}`}
                  />
                </div>
                {magicLinkForm.formState.errors.email && (
                  <p className="text-sm text-red-600 flex items-center gap-1 animate-shake">
                    <span>⚠️</span>
                    <span>{magicLinkForm.formState.errors.email.message}</span>
                  </p>
                )}
              </div>

              {/* Submit button */}
              <Button
                data-testid="login-magic-submit-btn"
                type="submit"
                className="w-full h-12 text-base font-semibold"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="inline-block animate-spin mr-2">⏳</span>
                    Envoi en cours...
                  </>
                ) : (
                  'Envoyer le lien magique'
                )}
              </Button>

              {/* Info text */}
              <p className="text-center text-sm text-muted-foreground">
                Un lien de connexion vous sera envoyé par email
              </p>

              {/* Back to password login */}
              <Button
                data-testid="login-back-to-password-btn"
                type="button"
                variant="ghost"
                onClick={() => setMode('password')}
                className="w-full text-base"
              >
                ← Retour à la connexion par mot de passe
              </Button>
            </form>
          )}
        </div>

        {/* Sign up link */}
        <div className="mt-6 text-center">
          <span className="text-sm text-muted-foreground">Pas encore de compte ? </span>
          <Link
            to="/register"
            className="text-sm text-primary hover:underline font-medium"
          >
            S'inscrire gratuitement
          </Link>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-muted-foreground">
          <p>Connexion sécurisée et chiffrée</p>
        </div>
      </div>
    </div>
  )
}
